// Mock product data for demonstration purposes
export interface Product {
  id: string
  title: string
  sku: string
  price: number
  inventory: number
  category: string
  imageUrl: string
  matchCategory: 'perfect-match' | 'potential-match' | 'no-match'
  skipped?: boolean
  skippedAt?: string
  amazonProduct?: AmazonProduct
}

interface AmazonProduct {
  id: string
  title: string
  asin: string
  price: number
  category: string
  imageUrl: string
  rating: number
  reviewCount: number
  matchConfidence?: number
}

const generateSKU = (category: string, index: number) => {
  const prefix = category.substring(0, 3).toUpperCase();
  return `${prefix}-${String(index).padStart(4, '0')}`;
}

const generatePrice = (basePrice: number) => {
  const variation = (Math.random() - 0.5) * 10;
  return Number((basePrice + variation).toFixed(2));
}

const generateProducts = () => {
  const products: Product[] = [];
  const categories = {
    Tech: [
      { title: 'Mechanical Keyboard', basePrice: 129.99, images: [
        'photo-1618384887929-16ec33fab9ef', // RGB keyboard
        'photo-1587829741301-dc798b83add3', // White keyboard
        'photo-1595225386386-79c3a7146e74', // Black keyboard
        'photo-1612198188060-c7c2a3b66eae', // Minimal keyboard
        'photo-1615869442320-fd02a129c77c'  // Gaming keyboard
      ]},
      { title: 'Gaming Mouse', basePrice: 79.99, images: [
        'photo-1615663245857-ac93bb7c39e7', // RGB mouse
        'photo-1527814050087-3793815479db', // Black mouse
        'photo-1625723044792-89fc8e3483cf', // White mouse
        'photo-1623820919239-0d0ff10797a1', // Gaming mouse
        'photo-1585770536735-27993a080586'  // Wireless mouse
      ]},
      { title: 'Webcam', basePrice: 89.99, images: [
        'photo-1587826080692-f439cd0b70da', // HD webcam
        'photo-1596566833669-733664c4d8fd', // Pro webcam
        'photo-1583071299210-c6c25209ee7e', // Streaming cam
        'photo-1542626991-cbc4e32524cc', // Conference cam
        'photo-1519810755548-39cd217da494'  // 4K webcam
      ]},
      { title: 'USB Microphone', basePrice: 149.99, images: [
        'photo-1598343672916-de13ab0636ed', // Studio mic
        'photo-1590602847861-f357a9332bbc', // Podcast mic
        'photo-1617575521317-d2966f2099d5', // Gaming mic
        'photo-1624958723474-5670d77e8946', // Pro mic
        'photo-1590483736622-bf5204e26d34'  // Streaming mic
      ]},
      { title: 'Monitor Stand', basePrice: 59.99, images: [
        'photo-1616210874411-de5ed76f47e7', // Aluminum stand
        'photo-1619506147154-01717498fc26', // Wood stand
        'photo-1587316205943-b15dc52a12e0', // Dual monitor
        'photo-1606229365485-93a3b8ee0385', // Ergonomic stand
        'photo-1593642632823-8f785ba67e45'  // Premium stand
      ]}
    ],
    Kitchen: [
      { title: 'Stand Mixer', basePrice: 299.99, images: [
        'photo-1594222082006-36a650c1dbcd', // Red mixer
        'photo-1591261730799-ee4e6c2d16d7', // White mixer
        'photo-1578653876428-6e6bf944f1f3', // Black mixer
        'photo-1593642634367-d91a135587b9', // Steel mixer
        'photo-1590794056226-79ef3a8147e1'  // Pro mixer
      ]},
      { title: 'Air Fryer', basePrice: 119.99, images: [
        'photo-1648149574316-dec59d14ac8a', // Digital fryer
        'photo-1639812110591-ddf621c8f45a', // Smart fryer
        'photo-1642484931008-62e24b182b02', // XL fryer
        'photo-1606914501449-5a96b6ce24ca', // Compact fryer
        'photo-1644703432635-2b86e5e2e2e1'  // Pro fryer
      ]},
      { title: 'Food Processor', basePrice: 149.99, images: [
        'photo-1625943553852-781c6dd46faa', // Pro processor
        'photo-1626200419199-391ae4be7773', // Compact processor
        'photo-1630459065645-549797a83dcf', // Premium processor
        'photo-1612445892832-79d3e75195c4', // Mini processor
        'photo-1628765189011-b458c3950c18'  // XL processor
      ]}
    ],
    Outdoor: [
      { title: 'Camping Tent', basePrice: 199.99, images: [
        'photo-1523987355523-c7b5b0dd90a7', // Family tent
        'photo-1504280390367-361c6d9f38f4', // Backpacking tent
        'photo-1537225228614-56cc3556d7ed', // 4-season tent
        'photo-1510312305653-8ed496efae75', // Ultralight tent
        'photo-1601134917279-ef70a0a90f18'  // Pop-up tent
      ]},
      { title: 'Hiking Backpack', basePrice: 89.99, images: [
        'photo-1622560480605-d83c853bc5c3', // Trail pack
        'photo-1580087256394-dc596e1c8f4f', // Day pack
        'photo-1609436132311-e4b0c9303b2a', // Adventure pack
        'photo-1580087257309-cf911fdd7d3c', // Ultra pack
        'photo-1580087266498-45e08e5cec8f'  // Trekking pack
      ]},
      { title: 'Solar Lantern', basePrice: 34.99, images: [
        'photo-1504233529578-6d46baba6d34', // LED lantern
        'photo-1563501089564-5d85c8ce0822', // Camp light
        'photo-1595432541891-a6b10cb5a887', // Solar lamp
        'photo-1621495488644-866e1a2b8f59', // Portable light
        'photo-1597774293578-9f12f2084e79'  // Emergency lamp
      ]}
    ],
    Home: [
      { title: 'Smart Speaker', basePrice: 99.99, images: [
        'photo-1589256469067-ea99122bbdc0', // Premium speaker
        'photo-1545454675-3531b543be5d', // Voice speaker
        'photo-1617396900799-f4ec2b43c7ae', // Smart home
        'photo-1619123707904-6026f5a1a2fc', // Mini speaker
        'photo-1608043152269-423dbba4e7e1'  // HD speaker
      ]},
      { title: 'Robot Vacuum', basePrice: 299.99, images: [
        'photo-1518640467707-6811f4a6ab73', // Smart vacuum
        'photo-1583845112239-290cb0831b54', // Auto cleaner
        'photo-1576986989720-bfe7f67b1d3e', // AI vacuum
        'photo-1579338559194-a162d19bf842', // Pro vacuum
        'photo-1562183241-840b8af0721e'  // Premium vacuum
      ]},
      { title: 'Air Purifier', basePrice: 179.99, images: [
        'photo-1626436629565-5f069a8ee6eb', // HEPA purifier
        'photo-1585771724684-38269d6639fd', // Smart purifier
        'photo-1587060159526-4c91d4a6ea8b', // Home purifier
        'photo-1585771724533-c4857fc1054f', // Premium purifier
        'photo-1561963404-7f9e71000362'  // UV purifier
      ]}
    ],
  };
  let id = 1;
  // Generate perfect matches (100)
  Object.entries(categories).forEach(([category, items]) => {
    items.forEach(item => {
      const variants = ['Pro', 'Elite', 'Ultimate', 'Premium', 'Max'];
      const colors = ['Midnight', 'Arctic', 'Forest', 'Desert', 'Ocean'];
      variants.forEach((variant, vIndex) => {
        colors.forEach((color, cIndex) => {
          if (products.length < 100) {
            const basePrice = item.basePrice;
            const price = generatePrice(basePrice);
            const title = `${color} ${item.title} ${variant}`;
            // Use different image for each variant/color combination
            const imageIndex = (vIndex + cIndex) % item.images.length;
            const imageId = item.images[imageIndex];
            products.push({
              id: String(id++),
              title,
              sku: generateSKU(category, id),
              price,
              inventory: Math.floor(Math.random() * 100) + 10,
              category,
              imageUrl: `https://images.unsplash.com/${imageId}?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80`,
              matchCategory: 'perfect-match',
              amazonProduct: {
                id: `amz${id}`,
                title: title + ' - Amazon Edition',
                asin: `B${Math.random().toString(36).substring(2, 11).toUpperCase()}`,
                price: price * (Math.random() * 0.2 + 0.9),
                category,
                imageUrl: `https://images.unsplash.com/${imageId}?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80`,
                rating: Number((Math.random() * 1.5 + 3.5).toFixed(1)),
                reviewCount: Math.floor(Math.random() * 2000) + 100,
              }
            });
          }
        });
      });
    });
  });
  // Generate potential matches (50) with unique images
  const potentialMatchImages = [
    'photo-1600940763585-f739a2ed8336', // Tech setup
    'photo-1585515320310-259814833e62', // Kitchen appliance
    'photo-1626585708001-a8cf41b7c4da', // Outdoor gear
    'photo-1585128903994-0b5e2659f4da', // Home device
    'photo-1581993192008-63e896f4f744', // Smart device
    'photo-1581993192000-e5b6c4e8fd85', // Premium device
    'photo-1593642634402-b0eb5e2eebc9', // Modern tech
    'photo-1593642634315-48f5414c3ad9', // Kitchen tool
    'photo-1593642634524-b40b5baae6bb', // Outdoor equipment
    'photo-1593642634627-6fdaf35c4d3c'  // Home automation
  ];
  for (let i = 0; i < 50; i++) {
    const categoryEntry = Object.entries(categories)[i % Object.keys(categories).length];
    const category = categoryEntry[0];
    const items = categoryEntry[1];
    const item = items[i % items.length];
    const imageId = potentialMatchImages[i % potentialMatchImages.length];
    const title = `${item.title} Special Edition`;
    const price = generatePrice(item.basePrice);
    products.push({
      id: String(id++),
      title,
      sku: generateSKU(category, id),
      price,
      inventory: Math.floor(Math.random() * 100) + 10,
      category,
      imageUrl: `https://images.unsplash.com/${imageId}?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80`,
      matchCategory: 'potential-match',
      amazonProduct: {
        id: `amz${id}`,
        title: title + ' - Similar Version',
        asin: `B${Math.random().toString(36).substring(2, 11).toUpperCase()}`,
        price: price * (Math.random() * 0.4 + 0.8),
        category,
        imageUrl: `https://images.unsplash.com/${imageId}?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80`,
        rating: Number((Math.random() * 2 + 3).toFixed(1)),
        reviewCount: Math.floor(Math.random() * 1000) + 50,
        matchConfidence: Number((Math.random() * 0.4 + 0.5).toFixed(2))
      }
    });
  }
  // Generate no matches (20)
  const noMatchItems = [
    { title: 'Smart Desk Pad', category: 'Tech', imageId: 'photo-1618384887929-16ec33fab9ef' },
    { title: 'Pasta Maker', category: 'Kitchen', imageId: 'photo-1590794056226-79ef3a8147e1' },
    { title: 'Camping Chair', category: 'Outdoor', imageId: 'photo-1513883049090-d31854380c2f' },
    { title: 'Wall Clock', category: 'Home', imageId: 'photo-1563861826100-9cb868fdbe1c' },
    { title: 'Cable Organizer', category: 'Tech', imageId: 'photo-1616210874411-de5ed76f47e7' },
  ];
  noMatchItems.forEach((item) => {
    const variants = ['Limited Edition', 'Custom', 'Exclusive', 'Special'];
    variants.forEach((variant) => {
      products.push({
        id: String(id++),
        title: `${item.title} ${variant}`,
        sku: generateSKU(item.category, id),
        price: generatePrice(99.99),
        inventory: Math.floor(Math.random() * 50) + 5,
        category: item.category,
        imageUrl: `https://images.unsplash.com/${item.imageId}?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80`,
        matchCategory: 'no-match'
      });
    });
  });
  // Add skipped products (10)
  const skippedItems = [
    { title: 'Charging Station', category: 'Tech', imageId: 'photo-1621972750749-0fbb1abb7736' },
    { title: 'Milk Frother', category: 'Kitchen', imageId: 'photo-1571474003682-28beb272b269' },
    { title: 'Water Filter', category: 'Outdoor', imageId: 'photo-1573733415144-45e1e0af6f58' },
    { title: 'Plant Stand', category: 'Home', imageId: 'photo-1485955900006-10f4d324d411' },
    { title: 'Door Camera', category: 'Home', imageId: 'photo-1558002038-1055907df827' },
  ];
  skippedItems.forEach((item, index) => {
    const daysAgo = Math.floor(Math.random() * 14) + 1;
    const skippedDate = new Date();
    skippedDate.setDate(skippedDate.getDate() - daysAgo);
    products.push({
      id: String(id++),
      title: item.title,
      sku: generateSKU(item.category, id),
      price: generatePrice(79.99),
      inventory: Math.floor(Math.random() * 50) + 5,
      category: item.category,
      imageUrl: `https://images.unsplash.com/${item.imageId}?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80`,
      matchCategory: 'no-match',
      skipped: true,
      skippedAt: skippedDate.toISOString()
    });
  });
  return products;
};

export const mockProductData: Product[] = generateProducts();